import {ChangeDetectionStrategy, Component, computed, effect, inject, input, ViewEncapsulation} from '@angular/core';
import {FormControl, FormGroup, FormGroupDirective} from "@angular/forms";
import {DiscountSelection} from "../../../../types";
import {HarbourDuesRotterdamResponse, HarbourDuesShippingCompany} from "@portbase/bezoekschip-service-typescriptmodels";
import {toSignal} from '@angular/core/rxjs-interop';
import {Observable} from "rxjs";
import {sendQuery} from "../../../../../../common/utils";
import {VisitContext} from "../../../../../visit-context";

@Component({
  selector: 'app-harbour-dues-discount',
  templateUrl: './discount.component.html',
  styleUrls: ['./discount.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DiscountComponent {
  readonly form = inject(FormGroupDirective);
  readonly discountGroup = this.form.control.get('discount') as FormGroup;
  readonly rotterdamGroup = this.discountGroup.get('rotterdam') as FormGroup;
  readonly isPartOfLinerService = this.rotterdamGroup.get('isPartOfLinerService') as FormControl;
  readonly shippingCompany = this.rotterdamGroup.get('shippingCompany') as FormControl;
  readonly linerServiceType = this.rotterdamGroup.get('linerServiceType') as FormControl;
  readonly twoIsOne = this.rotterdamGroup.get('twoIsOne') as FormControl;
  readonly selectedType = this.form.control.get('selectedType') as FormControl;

  readonly harbourDuesDetails = input.required<HarbourDuesRotterdamResponse>();

  private readonly partOfLinerServiceValue = toSignal(this.isPartOfLinerService.valueChanges, {
    initialValue: this.isPartOfLinerService.value
  });

  private readonly selectedTypeValue = toSignal(this.selectedType.valueChanges, {
    initialValue: this.selectedType.value
  });

  readonly discountSelected = computed(
    () => this.selectedTypeValue() === DiscountSelection.DISCOUNT
  );

  readonly linerServiceTypes = computed(
    () => this.harbourDuesDetails().lookupLinerServices
  );

  readonly linerServiceTypeCodes = computed(
    () => (this.linerServiceTypes() ?? []).map(linerService => linerService.linerServiceID)
  );

  readonly showLinerService = computed(() => this.partOfLinerServiceValue() === true);

  /** The visit's arrival carrier, when it is known to the SAP HBR system (has a businessPartnerIdSapHbr). */
  private readonly sapKnownVisitCarrier = computed(() => {
    const carrier = VisitContext.visit?.visitDeclaration?.arrivalVoyage?.carrier;
    return carrier?.businessPartnerIdSapHbr ? carrier : null;
  });

  /** Show the liner-service-discount banner only when the visit carrier qualifies for a discount. */
  readonly showLinerServiceDiscountBanner = computed(() => this.sapKnownVisitCarrier() !== null);

  shippingCompanyOptions: Observable<HarbourDuesShippingCompany[]> =
    sendQuery<HarbourDuesShippingCompany[]>("com.portbase.bezoekschip.common.api.visit.GetHarbourDuesShippingCompanies", {});

  /** The loaded shipping-company options, read as a signal so the preselect can react to them. */
  private readonly shippingCompanyOptionsSignal = toSignal(this.shippingCompanyOptions, {
    initialValue: [] as HarbourDuesShippingCompany[]
  });

  /**
   * Pre-selects the shipping company from the visit's arrival carrier when that carrier is known to
   * the SAP HBR system (it has a businessPartnerIdSapHbr). It waits for the shipping-company options
   * to load (read as a signal) and then selects the EXISTING option instance matching the carrier
   * (matched by businessPartnerIdSapHbr) rather than constructing a new object. If no option matches,
   * nothing is selected (the field stays blank) — we only ever list shipping companies we know. The
   * first guard makes it never overwrite a value already saved on the declaration, and also
   * self-disables the effect once a value is chosen (so re-runs are no-ops; no infinite loop and no
   * ExpressionChangedAfterItHasBeenChecked under Eager change detection).
   */
  constructor() {
    effect(() => {
      if (this.shippingCompany.value) {
        return;
      }
      const carrier = this.sapKnownVisitCarrier();
      if (!carrier) {
        return;
      }
      const loaded = this.shippingCompanyOptionsSignal();
      if (!loaded.length) {
        return;
      }
      const match = loaded.find(
        option => option.businessPartnerIdSapHbr === carrier.businessPartnerIdSapHbr
      );
      if (match) {
        this.shippingCompany.setValue(match);
      }
      // No match: leave the field blank — we only list shipping companies we know.
    });
  }

  shippingCompaniesEqual = (a: HarbourDuesShippingCompany, b: HarbourDuesShippingCompany): boolean =>
    a?.businessPartnerIdSapHbr === b?.businessPartnerIdSapHbr;

  linerServiceFormatter = (linerServiceId: string): string => {
    const match = this.linerServiceTypes()?.find(linerService => linerService.linerServiceID === linerServiceId);
    if (!match) {
      // No match: fall back to the raw id (never a stray " - ").
      return linerServiceId ?? "";
    }
    // Label = "<type description> - <description>". If either field is missing, show only the present
    // one so the label never has a dangling " - " on one side.
    return [match.linerServiceTypeDescription, match.description].filter(Boolean).join(" - ");
  };

  shippingCompanyFormatter = (shippingCompany: HarbourDuesShippingCompany): string =>
    shippingCompany?.name ?? '';
}
